import { connect, type ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'next/router';
import { AccountInformation } from '@/components/account/accountInformation';
import { ContactInformation } from '@/components/account/personalInformation';
import { StoredAddresses } from '@/components/account/storedAddresses';
import {
  StyledAccountContainer,
  StyledFlexedHeader,
  StyledLoyaltyContainer,
} from '@/components/account/styles';
import UpdatePassword from '@/components/account/updatePassword';
import { UserBirthday } from '@/components/account/userBirthday';
import { ConnectionErrorState } from '@/components/connectionError';
import Layout from '@/components/app/layout/index';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { RestrictedAccountRoute } from '@/components/restrict';
import { StyledPaddingContainer } from '@/components/uielements/forms';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { CSS_CLASSES } from '@/constants/cssClassNames';
import authActions from '@/redux/auth/actions';
import meActions from '@/redux/me/actions';
import { type RootState } from '@/types/app';
import DeleteAccount from '@/components/account/deleteAccount/deleteAccount';
import { Wallet } from '@/components/account/wallet';

export const ACCOUNT_CONNECTION_ERROR_TEST_ID = 'account-connection-error';

const AccountPage = ({
  logout,
  authLoading,
  fetchMe,
  showConnectionError,
  retryingProfile,
}: ReduxProps) => {
  return (
    <RestrictedAccountRoute>
      <Layout $backgroundOverride={true}>
        <StyledLoyaltyContainer>
          <StyledPaddingContainer>
            <StyledFlexedHeader className={CSS_CLASSES.LOYALTY.PAGE_HEADER}>
              {/* TODO - Move into own component? */}
              <StringAccessor tag="h2" accessor="account.header" html={true} />
              <StyledPrimaryButton
                data-css-override="StyledPrimaryButton"
                aria-label="Click to Sign Out"
                onClick={() => logout()}
                $buttonTheme="knockout"
                $submitting={authLoading}
              >
                <StringAccessor accessor="account.sign_out_button" />
              </StyledPrimaryButton>
            </StyledFlexedHeader>

            {/*
             * Contact info, birthday and Wallet (its stored-cards query is gated on the user
             * id) all hang off the profile, and `me.data` is not persisted — so on a fresh
             * load with the profile request failing at the network layer they rendered blank,
             * with no message and no retry anywhere on the page (TEAM-14780 QA, AC1). The
             * sections that fetch for themselves (stored addresses) cannot load over a dropped
             * connection either, so the shared connection-error state replaces the whole
             * block; a successful retry brings the profile back and Wallet then fetches on
             * its own.
             */}
            {showConnectionError ? (
              <ConnectionErrorState
                data-testid={ACCOUNT_CONNECTION_ERROR_TEST_ID}
                isRetrying={retryingProfile}
                onRetry={fetchMe}
              />
            ) : (
              <StyledAccountContainer>
                {/* Contact Info Section */}
                <ContactInformation />

                <Wallet />

                {/* Account Info Section */}
                <AccountInformation />

                {/* Stored Addresses Section */}
                <StoredAddresses />

                {/* User Birthday Section */}
                <UserBirthday />

                {/* Update Password Section */}
                <UpdatePassword />

                {/* Delete Account */}
                <DeleteAccount />
              </StyledAccountContainer>
            )}
          </StyledPaddingContainer>
        </StyledLoyaltyContainer>
      </Layout>
    </RestrictedAccountRoute>
  );
};

const mapStateToProps = (state: RootState) => ({
  authLoading: state.app.auth.loading,
  // Only when there is no profile to show: once one has loaded, a later failed refetch
  // leaves the sections on their last-known data (and Wallet has its own state).
  showConnectionError: state.app.me.meConnectionError && !state.app.me.data,
  // `me.loading` has other writers (the loyalty fetches), but none of them is dispatched on
  // /account while the profile is missing: fetchMeSaga only fans out to them on ME_SUCCESS,
  // and the rest come from rewards-page components. So here it tracks the profile retry,
  // which every outcome lowers (ME_SUCCESS / ME_FAIL / ME_CONNECTION_ERROR / ME_STOP_LOADING).
  retryingProfile: state.app.me.loading,
});

const mapDispatchToProps = {
  logout: authActions.logout,
  fetchMe: meActions.fetchMe,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default compose(connector, withRouter)(AccountPage);
